import React, { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, Trash2, ClipboardList } from 'lucide-react';
import { Modal, Button } from '../common';
import { AdminTestQuestion, TestQuestionMode, TestAnswerOption, UpdateTestQuestionData } from '../../types';

interface QuestionFormData {
  question: string;
  mode: TestQuestionMode;
  points: number;
  answers: { text: string }[];
  correctSingle?: string;
  correctMulti: Record<string, boolean>;
}

interface UpdateTestQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  question: AdminTestQuestion | null;
  onSubmit: (id: string, categoryId: string, data: UpdateTestQuestionData) => Promise<void>;
  isSubmitting: boolean;
}

function parseOptionsFromQuestion(q: AdminTestQuestion | null): {
  mode: TestQuestionMode;
  answers: TestAnswerOption[];
  correctSingle: string;
  correctMultiMap: Record<string, boolean>;
} {
  const fallback = {
    mode: 'single' as TestQuestionMode,
    answers: [{ id: 'a', text: '' }, { id: 'b', text: '' }],
    correctSingle: 'a',
    correctMultiMap: {},
  };

  if (!q) return fallback;

  const opts = q.options as { mode?: string; answers?: TestAnswerOption[] } | null;
  const mode: TestQuestionMode = opts?.mode === 'multi' ? 'multi' : 'single';
  const answers: TestAnswerOption[] = Array.isArray(opts?.answers) && opts!.answers!.length >= 2
    ? opts!.answers!
    : fallback.answers;

  const rawCorrect = q.correctAnswer;
  let correctSingle = answers[0]?.id ?? 'a';
  const correctMultiMap: Record<string, boolean> = {};

  if (mode === 'single') {
    if (typeof rawCorrect === 'string') correctSingle = rawCorrect;
    else if (Array.isArray(rawCorrect) && rawCorrect.length > 0) correctSingle = String(rawCorrect[0]);
  } else {
    const arr = Array.isArray(rawCorrect) ? rawCorrect : typeof rawCorrect === 'string' ? [rawCorrect] : [];
    arr.forEach((id) => { correctMultiMap[String(id)] = true; });
  }

  return { mode, answers, correctSingle, correctMultiMap };
}

export const UpdateTestQuestionModal: React.FC<UpdateTestQuestionModalProps> = ({
  isOpen,
  onClose,
  question,
  onSubmit,
  isSubmitting,
}) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm<QuestionFormData>({
    defaultValues: { mode: 'single', points: 1, answers: [{ text: '' }, { text: '' }], correctMulti: {} },
  });

  const { fields, append, remove, replace } = useFieldArray({ control, name: 'answers' });
  const mode = watch('mode');

  useEffect(() => {
    if (question && isOpen) {
      const { mode: m, answers, correctSingle, correctMultiMap } = parseOptionsFromQuestion(question);
      setValue('question', question.question);
      setValue('mode', m);
      setValue('points', question.points);
      setValue('correctSingle', correctSingle);
      setValue('correctMulti', correctMultiMap);
      replace(answers.map((a) => ({ text: a.text })));
    }
  }, [question, isOpen, setValue, replace]);

  const onSubmitForm = async (data: QuestionFormData) => {
    if (!question) return;

    const answers: TestAnswerOption[] = data.answers.map((a, i) => ({
      id: String.fromCharCode(97 + i),
      text: a.text.trim(),
    }));

    let correctAnswer: string | string[];
    if (data.mode === 'single') {
      correctAnswer = data.correctSingle || answers[0].id;
    } else {
      correctAnswer = answers
        .map((_, i) => String.fromCharCode(97 + i))
        .filter((id) => data.correctMulti[id]);
      if (correctAnswer.length === 0) {
        alert('Selecciona al menos una respuesta correcta.');
        return;
      }
    }

    await onSubmit(question.id, question.categoryId, {
      question: data.question.trim(),
      mode: data.mode,
      answers,
      correctAnswer,
      points: data.points ?? 1,
    });

    handleClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Editar Pregunta de Test" size="lg">
      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-5">

        {/* Enunciado */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">Enunciado *</label>
          <textarea
            {...register('question', { required: 'El enunciado es requerido' })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul resize-none"
          />
          {errors.question && <p className="mt-1 text-sm text-red-600">{errors.question.message}</p>}
        </div>

        {/* Tipo + Puntos */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Tipo de respuesta *</label>
            <select
              {...register('mode')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
            >
              <option value="single">Selección única</option>
              <option value="multi">Selección múltiple</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Puntos</label>
            <input
              type="number"
              min={1}
              max={10}
              {...register('points', { valueAsNumber: true, min: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
            />
          </div>
        </div>

        {/* Opciones */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-900">
              Opciones de respuesta *
              <span className="ml-1 text-xs text-gray-800/60 font-normal">
                ({mode === 'single' ? 'marca la correcta con el radio' : 'marca una o varias correctas'})
              </span>
            </label>
            {fields.length < 6 && (
              <button
                type="button"
                onClick={() => append({ text: '' })}
                className="flex items-center gap-1 text-xs text-gray-900 hover:underline"
              >
                <Plus className="w-3 h-3" /> Agregar opción
              </button>
            )}
          </div>

          <div className="space-y-2">
            {fields.map((field, index) => {
              const optionId = String.fromCharCode(97 + index);
              return (
                <div key={field.id} className="flex items-center gap-3">
                  {mode === 'single' ? (
                    <input
                      type="radio"
                      value={optionId}
                      {...register('correctSingle', { required: 'Selecciona la respuesta correcta' })}
                      className="w-4 h-4 text-brinsa-azul border-gray-300 flex-shrink-0"
                    />
                  ) : (
                    <input
                      type="checkbox"
                      {...register(`correctMulti.${optionId}`)}
                      className="w-4 h-4 text-brinsa-azul border-gray-300 rounded flex-shrink-0"
                    />
                  )}
                  <span className="w-5 text-xs font-semibold text-gray-800/70 flex-shrink-0 uppercase">
                    {optionId})
                  </span>
                  <input
                    type="text"
                    {...register(`answers.${index}.text`, { required: 'El texto es requerido' })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul text-sm"
                    placeholder={`Opción ${optionId.toUpperCase()}`}
                  />
                  {fields.length > 2 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="p-1.5 text-gray-800/50 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {mode === 'single' && errors.correctSingle && (
            <p className="mt-1 text-sm text-red-600">{errors.correctSingle.message}</p>
          )}
        </div>

        {/* Botones */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" isLoading={isSubmitting} leftIcon={<ClipboardList className="w-4 h-4" />}>
            Guardar Cambios
          </Button>
        </div>
      </form>
    </Modal>
  );
};
